import React from "react";
import { useDrag } from "react-dnd";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Divider,
  Avatar,
  LinearProgress,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  AttachFile as AttachFileIcon,
  ListAlt as ListAltIcon,
  Alarm as AlarmIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  MoreVert as MoreVertIcon,
  Timer as TimerIcon,
  QrCode as QrCodeIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  InsertDriveFile as InsertDriveFileIcon,
  GetApp as GetAppIcon,
} from "@mui/icons-material";

// Import ItemTypes
const ItemTypes = {
  TASK: "task",
};

// Light/Dark theme colors
const lightThemeColors = {
  primary: "#4361ee",
  secondary: "#3a0ca3",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#f8f9fd",
  card: "#ffffff",
  text: "#2d3748",
  textSecondary: "#718096",
  columnHeader: "#ebf3ff",
  columnBackground: "#ffffff",
  columnBackgroundHover: "#f7faff",
  taskBacklog: "#ffffff",
  taskInProgress: "#fff8f0",
  taskDone: "#f0fff4",
  taskBorder: "#e2e8f0",
  taskShadow: "0 4px 6px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.08)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.08)",
  headerBackground: "#ffffff",
  headerShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const darkThemeColors = {
  primary: "#4361ee",
  secondary: "#7b5cff",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#1a202c",
  card: "#2d3748",
  text: "#f7fafc",
  textSecondary: "#a0aec0",
  columnHeader: "#2d3748",
  columnBackground: "#2d3748",
  columnBackgroundHover: "#3a4556",
  taskBacklog: "#2d3748",
  taskInProgress: "#383245",
  taskDone: "#304035",
  taskBorder: "#4a5568",
  taskShadow: "0 4px 6px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.4)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)",
  headerBackground: "#2d3748",
  headerShadow: "0 1px 3px rgba(0,0,0,0.3)",
};

const TaskCard = ({
  task,
  onOpenAnr,
  columnId,
  onEditTask,
  isDarkMode,
  onDeleteTask,
  onArchiveTask,
  onStartTimer,
  onStopTimer,
  onResetTimer,
  onShowBarcode,
  onAddComment,
  onEditComment,
  onDeleteComment,
}) => {
  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;
  const [{ isDragging }, dragRef] = useDrag({
    type: ItemTypes.TASK,
    item: { type: ItemTypes.TASK, task, from: columnId },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });
  const user = useSelector((state) => state.user);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
  };

  const handleAssignAnrClick = (e) => {
    e.stopPropagation();
    onOpenAnr(task.id);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onEditTask(task);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    handleMenuClose(e);
    if (
      window.confirm("Sind Sie sicher, dass Sie diese Aufgabe löschen möchten?")
    ) {
      onDeleteTask(task.id);
    }
  };

  const handleArchiveClick = (e) => {
    e.stopPropagation();
    handleMenuClose(e);
    onArchiveTask(task.id);
  };

  const handleStartTimerClick = (e) => {
    e.stopPropagation();
    handleMenuClose(e);
    onStartTimer(task.id);
  };

  const handleStopTimerClick = (e) => {
    e.stopPropagation();
    handleMenuClose(e);
    onStopTimer(task.id);
  };

  const handleResetTimerClick = (e) => {
    e.stopPropagation();
    handleMenuClose(e);
    if (onResetTimer) {
      onResetTimer(task.id);
    }
  };

  const handleShowBarcodeClick = (e) => {
    e.stopPropagation();
    handleMenuClose(e);
    onShowBarcode && onShowBarcode(task);
  };

  // Determine card styles based on column
  let cardBackground;
  switch (columnId) {
    case "backlog":
      cardBackground = themeColors.taskBacklog;
      break;
    case "inprogress":
      cardBackground = themeColors.taskInProgress;
      break;
    case "done":
      cardBackground = themeColors.taskDone;
      break;
    default:
      cardBackground = themeColors.card;
  }

  // Check if task has documents
  const hasDocuments = task.documents && task.documents.length > 0;

  // Check if task has comments
  const hasComments = task.comments && task.comments.length > 0;

  // Determine if task is overdue
  const isOverdue =
    task.deadline &&
    new Date(task.deadline) < new Date() &&
    task.column !== "done";

  // Check if task is self-assigned
  const isSelfAssigned =
    task.assignedTo && user?.user?._id === task.assignedTo._id;

  // Calculate subtask progress
  const hasSubtasks = task.subTasks && task.subTasks.length > 0;
  const completedSubtasks = hasSubtasks
    ? task.subTasks.filter((st) => st.status === "completed").length
    : 0;
  const subtaskCompletionPercentage = hasSubtasks
    ? Math.round((completedSubtasks / task.subTasks.length) * 100)
    : 0;

  // Timer status
  const isTimerRunning =
    task.startTime &&
    (!task.endTime || new Date(task.startTime) > new Date(task.endTime));

  // Calculate elapsed time if timer is running
  const [elapsedTime, setElapsedTime] = React.useState("00:00:00");
  const [accumulatedTime, setAccumulatedTime] = React.useState(0);

  React.useEffect(() => {
    let interval;

    const calculateTimeDisplay = (milliseconds) => {
      const hours = Math.floor(milliseconds / (1000 * 60 * 60));
      const minutes = Math.floor(
        (milliseconds % (1000 * 60 * 60)) / (1000 * 60),
      );
      const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    // Calculate total accumulated time from previous timer sessions
    const getAccumulatedTime = () => {
      let total = 0;
      if (task.previousTimers && Array.isArray(task.previousTimers)) {
        total = task.previousTimers.reduce((sum, session) => {
          if (session.startTime && session.endTime) {
            return (
              sum + (new Date(session.endTime) - new Date(session.startTime))
            );
          }
          return sum;
        }, 0);
      }
      return total;
    };

    if (isTimerRunning) {
      // Timer is currently running - calculate current session + accumulated
      const previousSessionsTime = getAccumulatedTime();

      interval = setInterval(() => {
        // Calculate current session time from current startTime
        const startTime = new Date(task.startTime);
        const now = new Date();
        const currentSessionTime = now - startTime;

        // Total time is current session plus all previous sessions
        const totalMilliseconds = currentSessionTime + previousSessionsTime;
        setElapsedTime(calculateTimeDisplay(totalMilliseconds));
      }, 1000);
    } else if (task.startTime && task.endTime) {
      // Timer was stopped - show the accumulated time from previousTimers
      // This happens when timer is paused, so show the last recorded total
      const previousSessionsTime = getAccumulatedTime();

      // If we just finished a session, we should also have stored it
      // Check if the most recent previous timer includes the last endTime
      let lastSessionTime = 0;
      if (!task.previousTimers || !task.previousTimers.length) {
        // No previous timers tracked, so calculate from the last session
        // This is for backward compatibility - count the last start/end
        if (task.startTime && task.endTime) {
          lastSessionTime = new Date(task.endTime) - new Date(task.startTime);
        }
      }

      const totalMilliseconds = previousSessionsTime + lastSessionTime;
      setElapsedTime(calculateTimeDisplay(totalMilliseconds));
      setAccumulatedTime(totalMilliseconds);
    } else if (task.totalTime) {
      // If we have a pre-calculated total time (legacy support)
      setElapsedTime(calculateTimeDisplay(task.totalTime));
      setAccumulatedTime(task.totalTime);
    } else {
      // No timer data
      setElapsedTime("00:00:00");
      setAccumulatedTime(0);
    }

    return () => {
      clearInterval(interval);
    };
  }, [
    task.startTime,
    task.endTime,
    task.totalTime,
    task.previousTimers,
    isTimerRunning,
  ]);

  return (
    <Paper
      ref={dragRef}
      elevation={isDragging ? 6 : 1}
      sx={{
        bgcolor: cardBackground,
        p: 2.5,
        mb: 2.5,
        borderRadius: 3,
        opacity: isDragging ? 0.6 : 1,
        cursor: "move",
        border: isOverdue
          ? `1px solid ${themeColors.error}`
          : `1px solid ${
              isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.03)"
            }`,
        boxShadow: isDragging
          ? themeColors.taskShadowHover
          : themeColors.taskShadow,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: themeColors.taskShadowHover,
          transform: isDragging ? "none" : "translateY(-3px)",
        },
        position: "relative",
      }}
      onClick={handleEditClick}
    >
      {isOverdue && (
        <Chip
          label="Überfällig"
          size="small"
          color="error"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            fontSize: "0.7rem",
            fontWeight: "bold",
            borderRadius: "8px",
          }}
        />
      )}

      {/* Task ID Banner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          py: 0.5,
          px: 1,
          borderRadius: 1,
          bgcolor: isDarkMode
            ? "rgba(67, 97, 238, 0.15)"
            : "rgba(67, 97, 238, 0.08)",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="600"
          sx={{
            fontSize: "0.9rem",
            color: themeColors.primary,
          }}
        >
          {task.aufgabeId}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Barcode anzeigen">
          <IconButton
            size="small"
            onClick={(e) => handleShowBarcodeClick(e)}
            sx={{ color: themeColors.primary }}
          >
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Card Header with Title & Document Count */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{
            fontSize: "1rem",
            color: themeColors.text,
          }}
        >
          {task.title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          {hasSubtasks && (
            <Tooltip
              title={`${completedSubtasks}/${task.subTasks.length} Unteraufgaben erledigt`}
            >
              <Badge
                badgeContent={`${completedSubtasks}/${task.subTasks.length}`}
                color={
                  subtaskCompletionPercentage === 100 ? "success" : "primary"
                }
                sx={{ mr: 1 }}
              >
                <ListAltIcon
                  fontSize="small"
                  sx={{
                    color:
                      subtaskCompletionPercentage === 100
                        ? themeColors.success
                        : themeColors.primary,
                  }}
                />
              </Badge>
            </Tooltip>
          )}

          {/* Comments count */}
          {hasComments && (
            <Tooltip title={`${task.comments.length} Kommentar(e)`}>
              <Badge
                badgeContent={task.comments.length}
                color="info"
                sx={{ mx: 1 }}
              >
                <ChatBubbleOutlineIcon
                  fontSize="small"
                  sx={{ color: themeColors.info }}
                />
              </Badge>
            </Tooltip>
          )}

          {hasDocuments && (
            <Tooltip title={`${task.documents.length} Dokument(e) angehängt`}>
              <Badge
                badgeContent={task.documents.length}
                color="secondary"
                sx={{ ml: 1 }}
              >
                <AttachFileIcon
                  fontSize="small"
                  sx={{ color: themeColors.textSecondary }}
                />
              </Badge>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Task Description */}
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          color: themeColors.textSecondary,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minHeight: "60px",
        }}
      >
        {task.description || "Keine Beschreibung vorhanden"}
      </Typography>

      {/* Subtask Progress (if exists) */}
      {hasSubtasks && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={500}
              color={themeColors.textSecondary}
            >
              Unteraufgaben
            </Typography>
            <Typography variant="caption" color={themeColors.textSecondary}>
              {completedSubtasks}/{task.subTasks.length}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={subtaskCompletionPercentage}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              mb: 0.5,
              "& .MuiLinearProgress-bar": {
                bgcolor:
                  subtaskCompletionPercentage === 100
                    ? themeColors.success
                    : themeColors.primary,
              },
            }}
          />
        </Box>
      )}

      {/* Assignment info */}
      {task.assignedTo && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            p: 1,
            backgroundColor: isDarkMode
              ? "rgba(67, 97, 238, 0.1)"
              : "rgba(67, 97, 238, 0.05)",
            borderRadius: 2,
            border: isSelfAssigned
              ? `1px dashed ${themeColors.primary}`
              : "none",
          }}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: themeColors.primary,
              fontSize: "0.75rem",
              mr: 1,
            }}
          >
            {task.assignedTo.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: themeColors.primary }}
            >
              {task.assignedTo.name}
              {isSelfAssigned && " (Selbst)"}
            </Typography>
            {task.assignedDate && (
              <Typography variant="caption" color={themeColors.textSecondary}>
                Seit {new Date(task.assignedDate).toLocaleDateString("de-DE")}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Divider
        sx={{
          my: 1.5,
          borderColor: isDarkMode
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.06)",
        }}
      />

      {/* Date info */}
      <Box sx={{ mb: 2 }}>
        {task.deadline && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              icon={<AlarmIcon fontSize="small" />}
              label={new Date(task.deadline).toLocaleDateString("de-DE")}
              size="small"
              color={isOverdue ? "error" : "primary"}
              variant={isOverdue ? "filled" : "outlined"}
              sx={{
                borderRadius: "8px",
                height: "26px",
                "& .MuiChip-icon": {
                  fontSize: "1rem",
                },
                "& .MuiChip-label": {
                  px: 1,
                  fontSize: "0.7rem",
                  fontWeight: "medium",
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Documents Display */}
      {hasDocuments && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: isDarkMode
              ? "rgba(156, 39, 176, 0.1)"
              : "rgba(156, 39, 176, 0.05)",
            borderRadius: 2,
            border: `1px solid ${
              isDarkMode ? "rgba(156, 39, 176, 0.3)" : "rgba(156, 39, 176, 0.2)"
            }`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <AttachFileIcon
              fontSize="small"
              sx={{
                mr: 0.5,
                color: themeColors.secondary,
                fontSize: "0.9rem",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: themeColors.secondary,
                fontWeight: "medium",
                fontSize: "0.7rem",
              }}
            >
              Dokumente ({task.documents.length})
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {task.documents.map((doc, idx) => {
              const getFileIcon = (mimeType) => {
                if (mimeType.includes("pdf")) return PictureAsPdfIcon;
                if (mimeType.includes("image")) return ImageIcon;
                if (mimeType.includes("word") || mimeType.includes("document"))
                  return DescriptionIcon;
                return InsertDriveFileIcon;
              };

              const formatFileSize = (bytes) => {
                if (bytes < 1024) return bytes + " B";
                if (bytes < 1024 * 1024)
                  return (bytes / 1024).toFixed(1) + " KB";
                return (bytes / (1024 * 1024)).toFixed(1) + " MB";
              };

              const handleViewDocument = (e) => {
                e.stopPropagation();
                try {
                  // Open S3 URL in new tab
                  window.open(doc.s3Url, "_blank");
                } catch (error) {
                  console.error("Error viewing document:", error);
                }
              };

              const FileIcon = getFileIcon(doc.mimeType);

              return (
                <Box
                  key={doc._id || idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(255,255,255,0.7)",
                    borderRadius: 1,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(255,255,255,1)",
                      transform: "translateY(-1px)",
                      boxShadow: isDarkMode
                        ? "0 2px 8px rgba(0,0,0,0.3)"
                        : "0 2px 8px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={handleViewDocument}
                >
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <FileIcon
                      sx={{
                        mr: 1,
                        color: themeColors.secondary,
                        fontSize: "1.2rem",
                      }}
                    />
                    <Box sx={{ overflow: "hidden" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "medium",
                          fontSize: "0.7rem",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: themeColors.text,
                        }}
                      >
                        {doc.originalName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: themeColors.textSecondary,
                        }}
                      >
                        {formatFileSize(doc.size)}
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip title="Öffnen">
                    <IconButton
                      size="small"
                      sx={{
                        ml: 1,
                        color: themeColors.secondary,
                        width: 24,
                        height: 24,
                      }}
                      onClick={handleViewDocument}
                    >
                      <GetAppIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Email Attachments Display */}
      {task.emailAttachments && task.emailAttachments.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: isDarkMode
              ? "rgba(33, 150, 243, 0.1)"
              : "rgba(33, 150, 243, 0.05)",
            borderRadius: 2,
            border: `1px solid ${
              isDarkMode ? "rgba(33, 150, 243, 0.3)" : "rgba(33, 150, 243, 0.2)"
            }`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <EmailIcon
              fontSize="small"
              sx={{
                mr: 0.5,
                color: themeColors.info,
                fontSize: "0.9rem",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: themeColors.info,
                fontWeight: "medium",
                fontSize: "0.7rem",
              }}
            >
              E-Mail Anhänge ({task.emailAttachments.length})
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {task.emailAttachments.map((attachment, idx) => {
              const getFileIcon = (mimeType) => {
                if (mimeType.includes("pdf")) return PictureAsPdfIcon;
                if (mimeType.includes("image")) return ImageIcon;
                if (mimeType.includes("word") || mimeType.includes("document"))
                  return DescriptionIcon;
                return InsertDriveFileIcon;
              };

              const formatFileSize = (bytes) => {
                if (bytes < 1024) return bytes + " B";
                if (bytes < 1024 * 1024)
                  return (bytes / 1024).toFixed(1) + " KB";
                return (bytes / (1024 * 1024)).toFixed(1) + " MB";
              };

              const handleViewAttachment = (e) => {
                e.stopPropagation();
                try {
                  // Decode base64 content
                  const byteCharacters = atob(attachment.content);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  const blob = new Blob([byteArray], {
                    type: attachment.mimeType,
                  });

                  // Create URL and open in new tab
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank");

                  // Clean up the URL after a delay
                  setTimeout(() => URL.revokeObjectURL(url), 100);
                } catch (error) {
                  console.error("Error viewing attachment:", error);
                }
              };

              const FileIcon = getFileIcon(attachment.mimeType);

              return (
                <Box
                  key={attachment._id || idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(255,255,255,0.7)",
                    borderRadius: 1,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(255,255,255,1)",
                      transform: "translateY(-1px)",
                      boxShadow: isDarkMode
                        ? "0 2px 8px rgba(0,0,0,0.3)"
                        : "0 2px 8px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={handleViewAttachment}
                >
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <FileIcon
                      sx={{
                        mr: 1,
                        color: themeColors.info,
                        fontSize: "1.2rem",
                      }}
                    />
                    <Box sx={{ overflow: "hidden" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "medium",
                          fontSize: "0.7rem",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: themeColors.text,
                        }}
                      >
                        {attachment.filename}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: themeColors.textSecondary,
                        }}
                      >
                        {formatFileSize(attachment.size)}
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip title="Öffnen">
                    <IconButton
                      size="small"
                      sx={{
                        ml: 1,
                        color: themeColors.info,
                        width: 24,
                        height: 24,
                      }}
                      onClick={handleViewAttachment}
                    >
                      <GetAppIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1,
        }}
      >
        {task.projectId && task.projectId.ANR ? (
          <Chip
            icon={<BusinessIcon fontSize="small" />}
            label={task.projectId.ANR}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              maxWidth: "70%",
              borderRadius: "8px",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                px: 1,
                fontSize: "0.7rem",
                fontWeight: "medium",
              },
              "& .MuiChip-icon": {
                fontSize: "0.9rem",
              },
            }}
          />
        ) : (
          <Chip
            icon={<AssignmentIcon fontSize="small" />}
            label="Keine ANR"
            size="small"
            variant="outlined"
            color="default"
            sx={{
              borderRadius: "8px",
              bgcolor: "transparent",
              "& .MuiChip-label": {
                px: 1,
                fontSize: "0.7rem",
              },
              "& .MuiChip-icon": {
                fontSize: "0.9rem",
              },
            }}
          />
        )}

        <Chip
          icon={<AccessTimeIcon fontSize="small" />}
          label={(() => {
            // Calculate total hours from accumulated time
            const totalMilliseconds = accumulatedTime || 0;
            const hours = Math.floor(totalMilliseconds / (1000 * 60 * 60));
            const minutes = Math.floor(
              (totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60),
            );

            // Format as "Xh Ym" or just show hours if no minutes
            if (minutes > 0) {
              return `${hours}h ${minutes}m`;
            }
            return `${hours}h`;
          })()}
          size="small"
          variant="outlined"
          sx={{
            borderRadius: "8px",
            borderColor: themeColors.textSecondary,
            color: themeColors.textSecondary,
            "& .MuiChip-label": {
              px: 1,
              fontSize: "0.7rem",
              fontWeight: "medium",
            },
            "& .MuiChip-icon": {
              fontSize: "0.9rem",
              color: themeColors.textSecondary,
            },
          }}
        />
      </Box>

      {/* Task Timer Display */}
      {(task.startTime || isTimerRunning || task.totalTime) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mt: 2,
            bgcolor: isTimerRunning
              ? isDarkMode
                ? "rgba(243, 156, 18, 0.2)"
                : "rgba(243, 156, 18, 0.1)"
              : isDarkMode
                ? "rgba(46, 204, 113, 0.2)"
                : "rgba(46, 204, 113, 0.1)",
            padding: "12px",
            borderRadius: 2,
            border: `1px solid ${
              isTimerRunning
                ? isDarkMode
                  ? "rgba(243, 156, 18, 0.3)"
                  : "rgba(243, 156, 18, 0.2)"
                : isDarkMode
                  ? "rgba(46, 204, 113, 0.3)"
                  : "rgba(46, 204, 113, 0.2)"
            }`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TimerIcon
                fontSize="small"
                sx={{
                  mr: 1,
                  color: isTimerRunning
                    ? themeColors.warning
                    : themeColors.success,
                }}
              />
              <Typography
                variant="caption"
                fontWeight="medium"
                sx={{
                  color: isTimerRunning
                    ? themeColors.warning
                    : themeColors.success,
                }}
              >
                {isTimerRunning ? "Laufender Timer" : "Gesamtzeit"}
              </Typography>
            </Box>
            {isTimerRunning ? (
              <Tooltip title="Timer stoppen">
                <IconButton
                  size="small"
                  onClick={(e) => handleStopTimerClick(e)}
                  sx={{
                    color: themeColors.warning,
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                  }}
                >
                  <StopIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Timer starten">
                <IconButton
                  size="small"
                  onClick={(e) => handleStartTimerClick(e)}
                  sx={{
                    color: themeColors.success,
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                  }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Typography
            variant="h6"
            fontFamily="monospace"
            fontWeight="bold"
            align="center"
            sx={{
              color: isTimerRunning ? themeColors.warning : themeColors.success,
              letterSpacing: "1px",
            }}
          >
            {elapsedTime}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button
          size="small"
          variant="contained"
          onClick={handleAssignAnrClick}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            boxShadow: "none",
            fontWeight: 600,
            py: 0.75,
            flexGrow: 1,
            marginRight: 1,
          }}
        >
          {task.projectId && task.projectId.ANR ? "ANR ändern" : "ANR zuweisen"}
        </Button>

        {isTimerRunning ? (
          <IconButton
            onClick={handleStopTimerClick}
            sx={{
              bgcolor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              width: 32,
              height: 32,
              marginRight: 1,
            }}
          >
            <StopIcon fontSize="small" sx={{ color: themeColors.warning }} />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleStartTimerClick}
            sx={{
              bgcolor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              width: 32,
              height: 32,
              marginRight: 1,
            }}
          >
            <PlayArrowIcon
              fontSize="small"
              sx={{ color: themeColors.success }}
            />
          </IconButton>
        )}

        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{
            bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
            width: 32,
            height: 32,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{
            sx: {
              bgcolor: themeColors.card,
              color: themeColors.text,
              border: isDarkMode
                ? `1px solid ${themeColors.taskBorder}`
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 20px rgba(0,0,0,0.3)"
                : "0 4px 20px rgba(0,0,0,0.1)",
              "& .MuiMenuItem-root": {
                fontSize: "0.875rem",
                py: 1,
              },
            },
          }}
        >
          <MenuItem onClick={handleShowBarcodeClick}>
            <QrCodeIcon
              fontSize="small"
              sx={{ mr: 1.5, color: themeColors.primary }}
            />
            Barcode anzeigen
          </MenuItem>

          {/* Reset Timer Option */}
          {task.startTime && (
            <MenuItem onClick={handleResetTimerClick}>
              <RefreshIcon
                fontSize="small"
                sx={{ mr: 1.5, color: themeColors.warning }}
              />
              Timer zurücksetzen
            </MenuItem>
          )}

          <MenuItem onClick={handleArchiveClick}>
            <ArchiveIcon
              fontSize="small"
              sx={{ mr: 1.5, color: themeColors.info }}
            />
            Archivieren
          </MenuItem>

          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon
              fontSize="small"
              sx={{ mr: 1.5, color: themeColors.error }}
            />
            Löschen
          </MenuItem>
        </Menu>
      </Box>
    </Paper>
  );
};

export default TaskCard;
